// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuthState } from "@/providers/AuthProvider";
import { useSuperadminStatus } from "@/hooks/superadmin/useSuperadminStatus";
import { SuperadminLayout } from "@/components/superadmin/SuperadminLayout";
import { SuperadminService } from "@/services/superadmin";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/superadmin")({
  component: SuperadminGuard,
});

const STEP_UP_STORAGE_KEY = "pssmngr.superadmin.stepUpExpiresAt";

function readStepUpExpiry() {
  if (typeof window === "undefined") return 0;
  const value = Number(window.sessionStorage.getItem(STEP_UP_STORAGE_KEY));
  return Number.isFinite(value) ? value : 0;
}

function writeStepUpExpiry(expiresAt: number) {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(STEP_UP_STORAGE_KEY, String(expiresAt));
}

function SuperadminGuard() {
  const { t } = useTranslation("superadmin");
  const { status, isLoading: isAuthLoading } = useAuthState();
  const { data: superadminStatus, isLoading: isStatusLoading, error } =
    useSuperadminStatus();
  const navigate = useNavigate();
  const [stepUpExpiresAt, setStepUpExpiresAt] = useState(readStepUpExpiry);
  const [password, setPassword] = useState("");
  const [verificationError, setVerificationError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  const isLoading = isAuthLoading || isStatusLoading;
  const isStepUpValid = stepUpExpiresAt > Date.now();

  useEffect(() => {
    if (isLoading) return;

    if (status === "unauthenticated") {
      navigate({ to: "/login" });
      return;
    }

    // Only redirect to dashboard if definitively not a superadmin (FORBIDDEN)
    const isForbidden = error && typeof error === "object" && "code" in error && (error as { code: string }).code === "FORBIDDEN";
    if (isForbidden) {
      navigate({ to: "/dashboard" });
      return;
    }

    // If data loaded successfully and user is not superadmin
    if (superadminStatus && !superadminStatus.isSuperadmin) {
      navigate({ to: "/dashboard" });
    }
  }, [isLoading, status, superadminStatus, error, navigate]);

  const handleStepUpSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!password.trim()) {
      setVerificationError(t("common.passwordRequired"));
      return;
    }

    setIsVerifying(true);
    setVerificationError("");

    try {
      const result = await SuperadminService.verifyPassword(password);
      if (!result.valid) {
        setVerificationError(t("common.stepUpInvalid"));
        return;
      }

      writeStepUpExpiry(result.expiresAt);
      setStepUpExpiresAt(result.expiresAt);
      setPassword("");
    } catch {
      setVerificationError(t("common.stepUpError"));
      setPassword("");
    } finally {
      setIsVerifying(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
      </div>
    );
  }

  if (status === "unauthenticated" || !superadminStatus?.isSuperadmin) {
    return null;
  }

  if (!isStepUpValid) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4 py-8">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-3 text-center">
            <div className="mx-auto flex size-12 items-center justify-center rounded-lg border bg-muted text-muted-foreground">
              <ShieldCheck className="size-6" />
            </div>
            <div className="space-y-2">
              <CardTitle>{t("common.stepUpTitle")}</CardTitle>
              <CardDescription>{t("common.stepUpDescription")}</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleStepUpSubmit}>
              <div className="space-y-2">
                <Label htmlFor="superadmin-step-up-password">
                  {t("common.passwordRequired")}
                </Label>
                <Input
                  id="superadmin-step-up-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={t("common.stepUpPasswordPlaceholder")}
                  disabled={isVerifying}
                  autoFocus
                />
              </div>
              {verificationError ? (
                <p className="text-sm text-destructive" role="alert" aria-live="polite">
                  {verificationError}
                </p>
              ) : null}
              <Button type="submit" className="w-full" disabled={isVerifying}>
                {isVerifying ? (
                  <>
                    <Loader2 className="me-2 size-4 animate-spin" />
                    {t("common.stepUpVerifying")}
                  </>
                ) : (
                  t("common.verifyPassword")
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <SuperadminLayout />;
}
